import { type ChangeEvent, type FC } from "react";
import {
	Card,
	CardContent,
	FormControl,
	FormControlLabel,
	FormLabel,
	Radio,
	RadioGroup,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";

import { type ExternalAccessType } from "@/schemas";

import { type ProductItem } from "../../../types";
import { ScopeCheckboxList } from "../../../components/scope-checkbox-list";

interface ConnectionConfigStepProps {
	readonly connectionType: ExternalAccessType | undefined;
	readonly onConnectionTypeChange: (type: ExternalAccessType) => void;
	readonly name: string;
	readonly onNameChange: (name: string) => void;
	readonly products: ProductItem[];
	readonly onProductChange: (name: string, checked: boolean) => void;
	readonly hasNameError?: boolean;
	readonly hasProductError?: boolean;
	readonly hasTypeError?: boolean;
}

export const ConnectionConfigStep: FC<ConnectionConfigStepProps> = ({
	connectionType,
	onConnectionTypeChange,
	name,
	onNameChange,
	products,
	onProductChange,
	hasNameError,
	hasProductError,
	hasTypeError,
}) => {
	const intl = useIntl();

	return (
		<Stack gap={2}>
			<FormControl fullWidth error={hasNameError}>
				<FormLabel required sx={{ mb: 1 }}>
					<FormattedMessage id="Name" defaultMessage="Name" />
				</FormLabel>
				<TextField
					fullWidth
					value={name}
					onChange={(e: ChangeEvent<HTMLInputElement>) => onNameChange(e.target.value)}
					error={hasNameError}
					helperText={hasNameError ? <FormattedMessage id="Name is required" defaultMessage="Name is required" /> : ""}
					placeholder={intl.formatMessage({ id: "Some API connection...", defaultMessage: "Some API connection..." })}
					slotProps={{
						htmlInput: {
							"data-testid": "add-api-connection-modal__name__input",
						},
					}}
				/>
			</FormControl>

			<FormControl error={hasTypeError}>
				<FormLabel required sx={{ mb: 1 }}>
					<FormattedMessage id="Connection Type" defaultMessage="Connection Type" />
				</FormLabel>
				<RadioGroup
					row
					value={connectionType}
					onChange={(e) => onConnectionTypeChange(e.target.value as ExternalAccessType)}
					sx={{ gap: 2 }}
				>
					<Card
						variant="outlined"
						onClick={() => onConnectionTypeChange("apiKey")}
						sx={{
							flex: 1,
							cursor: "pointer",
							borderColor: connectionType === "apiKey" ? "primary.main" : undefined,
							"&:hover": { borderColor: "primary.main" },
						}}
					>
						<CardContent>
							<FormControlLabel
								value="apiKey"
								control={<Radio />}
								label={
									<Stack ml={0.5}>
										<Typography>
											<FormattedMessage id="Basic Authentication" defaultMessage="Basic Authentication" />
										</Typography>
										<Typography variant="caption">
											<FormattedMessage
												id="Generate an API key for simple authentication"
												defaultMessage="Generate an API key for simple authentication"
											/>
										</Typography>
									</Stack>
								}
							/>
						</CardContent>
					</Card>

					<Card
						variant="outlined"
						onClick={() => onConnectionTypeChange("oauth")}
						sx={{
							flex: 1,
							cursor: "pointer",
							borderColor: connectionType === "oauth" ? "primary.main" : undefined,
							bgcolor: connectionType === "oauth" ? "primary.50" : undefined,
							"&:hover": { borderColor: "primary.main" },
						}}
					>
						<CardContent>
							<FormControlLabel
								value="oauth"
								control={<Radio />}
								label={
									<Stack ml={0.5}>
										<Typography>
											<FormattedMessage id="OAuth Connection" defaultMessage="OAuth Connection" />
										</Typography>
										<Typography variant="caption">
											<FormattedMessage
												id="Configure OAuth credentials for secure authentication"
												defaultMessage="Configure OAuth credentials for secure authentication"
											/>
										</Typography>
									</Stack>
								}
							/>
						</CardContent>
					</Card>
				</RadioGroup>
			</FormControl>

			<FormControl error={hasProductError}>
				<FormLabel required>
					<FormattedMessage
						id="Select which services this connection will be used for"
						defaultMessage="Select which services this connection will be used for"
					/>
				</FormLabel>
				<ScopeCheckboxList scopes={products} onChange={onProductChange} />
				{hasProductError === true && (
					<Typography variant="caption" color="error" sx={{ mt: 1 }}>
						<FormattedMessage
							id="At least one service must be selected"
							defaultMessage="At least one service must be selected"
						/>
					</Typography>
				)}
			</FormControl>
		</Stack>
	);
};
